
import React, { useState, useEffect } from "react";
import { useWidgetDefinitions } from "@/hooks/useWidgetDefinitions";
import { WidgetDefinition } from "@/components/admin/dashboard/widget-types";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { WidgetsList } from "./WidgetsList";
import { WidgetForm } from "./WidgetForm";
import { Button } from "@/components/ui/button";
import { Plus, RefreshCw, DatabaseBackup } from "lucide-react";
import { toast } from "@/hooks/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export function WidgetDesigner() {
  const {
    definitions,
    isLoading,
    fetchWidgetDefinitions,
    createWidgetDefinition,
    updateWidgetDefinition,
    deleteWidgetDefinition,
    toggleWidgetActive,
    initializeDefaultWidgets,
  } = useWidgetDefinitions();

  const [selectedWidget, setSelectedWidget] = useState<WidgetDefinition | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTab, setActiveTab] = useState("list");
  const [refreshing, setRefreshing] = useState(false);
  const [isInitDialogOpen, setIsInitDialogOpen] = useState(false);

  useEffect(() => {
    // Load all widget definitions when component mounts
    fetchWidgetDefinitions();
    console.log("WidgetDesigner: Loading widget definitions");
  }, [fetchWidgetDefinitions]);

  useEffect(() => {
    console.log(`WidgetDesigner: Loaded ${definitions.length} widget definitions`);
  }, [definitions]);

  const handleSelectWidget = (widget: WidgetDefinition) => {
    setSelectedWidget(widget);
    setIsCreating(false);
    setActiveTab("edit");
  };

  const handleCreateNew = () => {
    setSelectedWidget(null);
    setIsCreating(true);
    setActiveTab("edit");
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchWidgetDefinitions();
    toast({
      title: "Refreshed",
      description: "Widget definitions refreshed from database",
    });
    setRefreshing(false);
  };

  const handleInitDefaults = async () => {
    setIsInitDialogOpen(false);
    setRefreshing(true);
    await initializeDefaultWidgets();
    setRefreshing(false);
  };

  const handleSave = async (widgetData: Partial<WidgetDefinition>) => {
    let success = false;

    if (isCreating) {
      const newWidget = await createWidgetDefinition({
        id: generateWidgetId(widgetData.title || "widget"),
        title: widgetData.title || "New Widget",
        description: widgetData.description || "",
        defaultSize: widgetData.defaultSize || "medium",
        chartType: widgetData.chartType || "stat",
        dashboardType: widgetData.dashboardType || "admin",
        queryName: widgetData.queryName,
        dataFields: widgetData.dataFields || [],
        isActive: true,
        customComponent: widgetData.customComponent,
      });

      if (newWidget) {
        success = true;
        setSelectedWidget(newWidget);
        setIsCreating(false);
      }
    } else if (selectedWidget) {
      success = await updateWidgetDefinition(selectedWidget.id, widgetData);
      if (success) {
        const updatedWidget = definitions.find(w => w.id === selectedWidget.id);
        if (updatedWidget) {
          setSelectedWidget({...updatedWidget, ...widgetData});
        }
      }
    }

    if (success) {
      setActiveTab("list");
    }
  };

  const handleDelete = async (id: string): Promise<boolean> => {
    return await deleteWidgetDefinition(id).then(success => {
      if (success && selectedWidget && selectedWidget.id === id) {
        setSelectedWidget(null);
        setActiveTab("list");
      }
      return success;
    });
  };

  const handleToggleActive = async (id: string): Promise<boolean> => {
    return await toggleWidgetActive(id).then(success => {
      if (success && selectedWidget && selectedWidget.id === id) {
        const widget = definitions.find(w => w.id === id);
        if (widget) {
          setSelectedWidget({...widget, isActive: !widget.isActive});
        }
      }
      return success;
    });
  };

  const generateWidgetId = (title: string): string => {
    const base = title
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '');
    
    const existing = definitions.map(d => d.id);
    let id = base;
    let counter = 1;
    
    while (existing.includes(id)) {
      id = `${base}-${counter}`;
      counter++;
    }
    
    return id;
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold tracking-tight">Widget Designer</h2>
        <div className="flex gap-2">
          <Button onClick={() => setIsInitDialogOpen(true)} variant="outline">
            <DatabaseBackup className="mr-2 h-4 w-4" />
            Reset to Defaults
          </Button>
          <Button onClick={handleRefresh} variant="outline">
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
          <Button onClick={handleCreateNew}>
            <Plus className="mr-2 h-4 w-4" />
            Create New Widget
          </Button>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="list">Widgets List</TabsTrigger>
          <TabsTrigger value="edit" disabled={!isCreating && !selectedWidget}>
            {isCreating ? "Create Widget" : "Edit Widget"}
          </TabsTrigger>
        </TabsList>
        
        <TabsContent value="list" className="mt-4">
          <WidgetsList 
            widgets={definitions}
            isLoading={isLoading}
            onSelect={handleSelectWidget}
            onToggleActive={handleToggleActive}
            onDelete={handleDelete}
          />
        </TabsContent>
        
        <TabsContent value="edit" className="mt-4">
          <WidgetForm 
            widget={isCreating ? null : selectedWidget}
            onSave={handleSave}
            onCancel={() => setActiveTab("list")}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={isInitDialogOpen} onOpenChange={setIsInitDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Reset to Default Widgets</AlertDialogTitle>
            <AlertDialogDescription>
              This will add all the default admin and user widgets to the database if they don't exist.
              Existing widgets will not be modified. This is useful if widgets are missing.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleInitDefaults}>
              Reset to Defaults
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
